import React, { memo, useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Player } from '@lottiefiles/react-lottie-player';
import { Text } from '@Sage/hoi-poi-ui';

const ANIMATION_SPEED = 1.75;

const DesignOption = memo(({ title, description, animation, textColor, onClick }) => {
    const playerRef = useRef(null);
    const lottieRef = useRef(null);
    const directionRef = useRef(1);
    const [isHovered, setIsHovered] = useState(false);

    const playInDirection = useCallback((direction) => {
        directionRef.current = direction;
        playerRef.current?.setPlayerSpeed?.(ANIMATION_SPEED);
        playerRef.current?.setPlayerDirection?.(direction);
        lottieRef.current?.setSpeed?.(ANIMATION_SPEED);
        lottieRef.current?.setDirection?.(direction);
        playerRef.current?.play();
    }, []);

    const moveToIdleFrame = useCallback(() => {
        const totalFrames = lottieRef.current?.totalFrames;
        const firstFrame = lottieRef.current?.firstFrame || 0;

        if (!totalFrames || !lottieRef.current) return;

        const middleFrame = Math.max(firstFrame + Math.floor((totalFrames - 1) / 2), 0);

        lottieRef.current.goToAndStop(middleFrame, true);
    }, []);

    const handleMouseEnter = useCallback(() => {
        setIsHovered(true);
        playInDirection(1);
    }, [playInDirection]);

    const handleMouseLeave = useCallback(() => {
        setIsHovered(false);
        directionRef.current = 1;
        playerRef.current?.stop();
        moveToIdleFrame();
    }, [moveToIdleFrame]);

    const handlePlayerEvent = useCallback(
        (event) => {
            if (event === 'load' || event === 'ready') {
                playerRef.current?.setPlayerSpeed?.(ANIMATION_SPEED);
                lottieRef.current?.setSpeed?.(ANIMATION_SPEED);
                moveToIdleFrame();
                return;
            }

            if (event === 'complete') {
                if (isHovered) {
                    playInDirection(directionRef.current * -1);
                    return;
                }

                moveToIdleFrame();
            }
        },
        [isHovered, moveToIdleFrame, playInDirection],
    );

    const handleSelect = useCallback(() => {
        onClick?.();
    }, [onClick]);

    const handleKeyDown = useCallback(
        (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleSelect();
            }
        },
        [handleSelect],
    );

    return (
        <div
            className="marketing-campaigns__design-form-option"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handleSelect}
            onKeyDown={handleKeyDown}
            role="button"
            tabIndex={0}
        >
            <div className="marketing-campaigns__design-form-option-icon">
                <Player
                    ref={playerRef}
                    lottieRef={(instance) => {
                        lottieRef.current = instance;
                    }}
                    src={animation}
                    speed={ANIMATION_SPEED}
                    onEvent={handlePlayerEvent}
                    keepLastFrame
                    className="marketing-campaigns__design-form-option-lottie"
                />
            </div>
            <div className="marketing-campaigns__design-form-option-copy">
                <Text
                    type="subtitle"
                    medium
                    className="marketing-campaigns__design-form-option-title"
                >
                    {title}
                </Text>
                <Text type="caption" color={textColor}>
                    {description}
                </Text>
            </div>
        </div>
    );
});

DesignOption.displayName = 'DesignOption';

DesignOption.propTypes = {
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    animation: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
    textColor: PropTypes.string,
    onClick: PropTypes.func,
};

export default DesignOption;
